'use client'

import { memo, useState } from 'react'
import { DayPickerProps } from 'react-day-picker'

import { cn } from '@/lib/utils'
import { useStoreI18n } from '@/store'
import { Icon, InputFake, Popover } from '@/ui/components'
import { addYears, endOfYear, format } from 'date-fns'

import { Calendar } from '@/ui/Shadcn/calendar'

interface DatePickerProps extends Omit<DayPickerProps, 'selected'> {
  label: string
  placeholder?: string
  description?: string
  name?: string
  disabled?: boolean
  inputClassName?: string
  mode?: 'single'
  selected?: Date
  required?: boolean
  autoComplete?: string
  formattedFormat?: 'dd.mm.yyyy' | 'dd.MM.yyyy' | 'dd-MM-yyyy' | 'yyyy-MM-dd'
  valueFormat?: 'dd.mm.yyyy' | 'dd.MM.yyyy' | 'dd-MM-yyyy' | 'yyyy-MM-dd'
  minDate?: Date
  maxDate?: Date
  open?: boolean
  className?: string
  onOpenChange?: (open: boolean) => void
  onDateSelect?: (date: Date) => void
  onDateClear?: () => void
}

export function DatePicker({
  label,
  placeholder = 'dd.mm.yyyy',
  formattedFormat = 'dd.MM.yyyy',
  valueFormat = 'dd.MM.yyyy',
  description,
  id = '',
  name,
  mode = 'single',
  selected,
  inputClassName,
  required = false,
  autoComplete = 'off',
  disabled = false,
  minDate,
  maxDate,
  open,
  onOpenChange,
  onDateSelect,
  onDateClear,
  className,
  ...props
}: DatePickerProps) {
  const [date, setDate] = useState<Date | undefined>(selected)
  const [previousSelected, setPreviousSelected] = useState<Date | undefined>(
    selected
  )
  if (selected !== previousSelected) {
    setPreviousSelected(selected)
    setDate(selected)
  }
  const formattedValue = date ? format(new Date(date), formattedFormat) : ''
  const value = date ? format(new Date(date), valueFormat) : ''

  const locale = useStoreI18n.use.currentLocale()
  const [popoverOpen, setPopoverOpen] = useState(false)
  const popoverIsOpen = disabled ? false : (open ?? popoverOpen)
  const normalizedMinDate = minDate
    ? new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())
    : undefined
  const normalizedMaxDate = maxDate
    ? new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())
    : undefined
  const disabledMatchers = [
    ...(normalizedMinDate ? [{ before: normalizedMinDate }] : []),
    ...(normalizedMaxDate ? [{ after: normalizedMaxDate }] : [])
  ]
  // With captionLayout="dropdown" react-day-picker bounds the year dropdown to
  // [startMonth, endMonth] and defaults endMonth to the end of the current year,
  // making 2027+ unselectable. Derive the bounds from min/max, falling back to a
  // generous future window so future dates stay selectable.
  const navStartMonth = normalizedMinDate
  const navEndMonth = normalizedMaxDate ?? endOfYear(addYears(new Date(), 10))

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled && nextOpen) {
      return
    }
    if (open === undefined) {
      setPopoverOpen(nextOpen)
    }
    onOpenChange?.(nextOpen)
  }

  const handleClearDate = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setDate(undefined)
    handleOpenChange(false)
    onDateClear?.()
  }

  return (
    <div className={cn('relative flex flex-col gap-3', className)}>
      {/* Visually-hidden real input: `type=hidden`/`readonly` are barred from
          HTML5 constraint validation, so `required` would be ignored. This
          keeps the value in FormData AND participates in native validation. */}
      <input
        id={id}
        name={name}
        type="text"
        tabIndex={-1}
        aria-hidden
        autoComplete={autoComplete}
        required={required}
        value={value}
        disabled={disabled}
        onChange={() => {}}
        className="peer pointer-events-none absolute bottom-0 left-0 h-px w-px border-0 p-0 opacity-0"
      />

      <Popover
        triggerClassName={
          'input-focus peer-focus:border-neutral-300 peer-focus:bg-neutral-300'
        }
        className={'z-1000005 w-auto overflow-hidden p-0'}
        open={popoverIsOpen}
        onOpenChange={handleOpenChange}
        button={
          <InputFake
            active={popoverIsOpen}
            label={label}
            value={formattedValue}
            placeholder={placeholder}
            className={inputClassName}
            disabled={disabled}
          />
        }
      >
        <Calendar
          mode={mode}
          selected={date}
          defaultMonth={date}
          captionLayout="dropdown"
          weekStartsOn={1}
          startMonth={navStartMonth}
          endMonth={navEndMonth}
          id={id}
          formatters={{
            formatMonthDropdown: (date) =>
              date.toLocaleString(locale, { month: 'short' }),
            formatWeekdayName: (date) =>
              date.toLocaleString(locale, { weekday: 'short' })
          }}
          disabled={
            disabled === true
              ? true
              : disabledMatchers.length > 0
                ? disabledMatchers
                : undefined
          }
          onSelect={(nextDate) => {
            handleOpenChange(false)
            if (!nextDate) {
              return
            }
            setDate(nextDate)
            onDateSelect?.(nextDate)
          }}
          {...props}
        />
      </Popover>
      {date && !disabled && (
        <button
          type="button"
          aria-label="Clear date"
          className="global-focus absolute -top-1 -right-1 z-2 flex size-4.5 items-center justify-center rounded-full bg-neutral-100 text-neutral-800"
          onClick={handleClearDate}
        >
          <Icon className="size-4" icon="material-symbols:close" />
        </button>
      )}
      {description && (
        <div className="text-muted-foreground px-1 text-sm">
          {description}{' '}
          <span className="font-medium">
            {date ? format(new Date(date), formattedFormat) : ''}
          </span>
          .
        </div>
      )}
    </div>
  )
}

DatePicker.displayName = 'DatePicker'

export default memo(DatePicker)
